import { useCreateFolder, useUpdateFolder } from "@/api/folders";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  FOLDER_RESOURCE_TYPE_CANVAS,
  type Folder,
  type FolderResourceType,
} from "@cohesive/api-client";
import { useState } from "react";
import toast from "react-hot-toast";

export interface CanvasFolderDialogProps {
  onClose: () => void;
  workspaceId: string;
  /** Parent for a newly created folder (null = workspace root). Ignored in rename. */
  parentFolderId?: string | null;
  /** When set, the dialog renames this folder instead of creating one. */
  folder?: Folder;
  /** The kind of resource the folder organizes. Defaults to canvas. */
  resourceType?: FolderResourceType;
  onSuccess?: () => void;
}

/** Create a new resource folder (canvas or chat), or rename an existing one. */
export function CanvasFolderDialog({
  onClose,
  workspaceId,
  parentFolderId = null,
  folder,
  resourceType = FOLDER_RESOURCE_TYPE_CANVAS,
  onSuccess,
}: CanvasFolderDialogProps) {
  const isRename = !!folder;
  const [name, setName] = useState(folder?.name ?? "");

  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder(workspaceId);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    if (isRename) {
      updateFolder.mutate(
        { folderId: folder.id, data: { name: trimmed } },
        {
          onSuccess: () => {
            toast.success("Folder renamed");
            onSuccess?.();
            onClose();
          },
          onError: () => toast.error("Failed to rename folder"),
        },
      );
      return;
    }

    createFolder.mutate(
      {
        resource_type: resourceType,
        workspace_id: workspaceId,
        parent_folder_id: parentFolderId ?? undefined,
        name: trimmed,
      },
      {
        onSuccess: () => {
          toast.success("Folder created");
          onSuccess?.();
          onClose();
        },
        onError: () => toast.error("Failed to create folder"),
      },
    );
  };

  const isPending = createFolder.isPending || updateFolder.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isRename ? "Rename Folder" : "New Folder"}</DialogTitle>
          <DialogDescription>
            {isRename
              ? "Enter a new name for this folder."
              : "Enter a name for the new folder."}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Folder name"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isPending}>
            {isPending
              ? isRename
                ? "Saving..."
                : "Creating..."
              : isRename
                ? "Save"
                : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
